import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FlatButton } from '@travel/ui';

import DataLayer from 'components/DataLayer';

import { pushLocation } from 'store/__router/actions';
import { getRouter } from 'store/__router/selectors';
import { getErrors, getIsFetching, getItems } from 'store/reservationEntrance/selectors';
import { setDefaultSEOData } from 'store/seo/actions';

import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import useLogin from 'hooks/useLogin';
import usePageLoaderOnInitial from 'hooks/usePageLoaderOnInitial';

import ReservationCheckingForm from './components/reservationCheckingForm';

import styles from './reservationEntrancePage.module.scss';

type Props = {
  query?: {
    callback?: string;
  };
};

export type AuthData = {
  bookingId?: string;
  confirmationKey?: string;
};

function ReservationEntrancePage(props: Props) {
  const dispatch = useDispatch();
  const error = useSelector(getErrors);
  const { isAuthenticated, token } = useSelector(getItems);
  const isFetching = useSelector(getIsFetching);
  const router = useSelector(getRouter);
  const {
    location: { location },
    baseUrl,
  } = router;
  const { onClickLogin } = useLogin();
  const [submitted, setSubmitted] = useState(false);

  const pageName =
    error?.status && submitted
      ? 'booking:wrong_booking_id_or_confirmation_key_message'
      : 'booking:booking_id_and_confirmation_key_form';

  const title = <Translate id="Reservation_List.Booked_As_A_Guest" />;
  const subtitle = <Translate id="Reservation_List.Guest_Enter_Details_To_See" />;

  usePageLoaderOnInitial('reservationEntrance', isFetching);

  useEffect(() => {
    dispatch(setDefaultSEOData());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && token) {
      dispatch(pushLocation({ pathname: paths.reservationList.path }));
    }
  }, [dispatch, isAuthenticated, token]);

  const redirectToLogin = () => {
    return onClickLogin(baseUrl + location.pathname + location.search);
  };

  const checkReservationFormSubmitted = (submitted: boolean) => {
    setSubmitted(submitted);
  };

  return (
    <div
      data-testid="reservationEntrancePage-wrapper"
      className={styles.reservationEntranceWrapper}
    >
      <div className={styles.memberLogin} data-testid="memberLogin">
        <h1 className={styles.title}>
          <Translate id="Reservation_List.Booked_As_A_Member" />
        </h1>
        <p className={styles.content}>
          <Translate id="Reservation_List.Member_Login_To_See" />
        </p>
        <FlatButton className={styles.button} type={'button'} onClick={redirectToLogin}>
          <Translate id="Member_Login.Login.Login_Button" />
        </FlatButton>
      </div>
      <div className={styles.guestBooking} data-testid="guestLogin">
        <div className={styles.title}>{title}</div>
        <p className={styles.subtitle}>{subtitle}</p>
        <ReservationCheckingForm
          query={props.query}
          checkReservationFormSubmitted={checkReservationFormSubmitted}
        />
      </div>

      <DataLayer pageName={pageName} pageType="cart_checkout" siteSection="booking" />
    </div>
  );
}

export default ReservationEntrancePage;
